/**
 * Icon Component
 *
 * A flexible icon component for custom SVG icons.
 * Supports all design token colors and Tailwind classes.
 *
 * @example
 * // Basic usage
 * import ArrowIcon from 'src/assets/arrow.svg';
 * <Icon icon={ArrowIcon} size={24} />
 *
 * @example
 * // With color from design tokens
 * import CheckIcon from 'src/assets/check.svg';
 * <Icon icon={CheckIcon} size={20} colorClass="text-icon-success" />
 *
 * @example
 * // With hover states
 * import HeartIcon from 'src/assets/heart.svg';
 * <Icon
 *   icon={HeartIcon}
 *   size={24}
 *   colorClass="text-icon-secondary hover:text-icon-brand"
 * />
 *
 * @example
 * // Inherit color from parent
 * <button className="text-icon-primary">
 *   <Icon icon={PlusIcon} size={16} />
 * </button>
 *
 * @example
 * // With additional Tailwind classes
 * <Icon
 *   icon={LoaderIcon}
 *   size={20}
 *   colorClass="text-icon-brand"
 *   className="animate-spin"
 * />
 */
import * as React from 'react';
import { cn } from 'src/utils/utils';

export interface IconProps extends React.SVGAttributes<SVGElement> {
  /**
   * SVG icon component.
   * Import your SVG file and pass it to this prop.
   *
   * @example
   * import MyIcon from 'src/assets/my-icon.svg';
   * <Icon icon={MyIcon} />
   */
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;

  /**
   * Icon size - can be either a number (pixels) or a size class from design tokens.
   * When using size classes, both width and height are set automatically.
   *
   * @example
   * // Using numeric size (pixels)
   * size={24}
   *
   * @example
   * // Using design token size classes
   * sizeClass="h-m w-m"    // 16x16px
   * sizeClass="h-l w-l"    // 20x20px
   * sizeClass="h-xl w-xl"  // 24x24px
   *
   * @default 24 (if neither size nor sizeClass is provided)
   */
  size?: number;

  /**
   * Size class from design tokens.
   * Use this for responsive sizing with design system values.
   * Takes precedence over the numeric size prop.
   *
   * Available sizes from design tokens:
   * - h-xs w-xs (8x8px)
   * - h-s w-s (12x12px)
   * - h-m w-m (16x16px)
   * - h-l w-l (20x20px)
   * - h-xl w-xl (24x24px)
   * - h-2xl w-2xl (32x32px)
   * - h-3xl w-3xl (40x40px)
   *
   * @example
   * sizeClass="h-m w-m"
   * sizeClass="h-xl w-xl"
   */
  sizeClass?: string;

  /**
   * Color class from design tokens.
   * If not provided, inherits color from parent.
   *
   * @example
   * colorClass="text-icon-primary"
   * colorClass="text-icon-brand hover:text-icon-brand-hover"
   * colorClass="text-text-error"
   */
  colorClass?: string;

  /**
   * Additional CSS classes.
   * Use for animations, transforms, etc.
   *
   * @example
   * className="animate-spin"
   * className="rotate-45"
   */
  className?: string;

  /**
   * Stroke width for the icon.
   * Only works if your SVG uses stroke.
   * @default 2
   */
  strokeWidth?: number;
}

const Icon = React.forwardRef<SVGSVGElement, IconProps>(
  (
    {
      icon: IconComponent,
      size = 8,
      sizeClass,
      colorClass,
      className,
      strokeWidth = 2,
      ...props
    },
    ref
  ) => {
    // If sizeClass is provided, use Tailwind classes for sizing
    // Otherwise, use numeric size with width/height attributes
    const baseClasses = cn(
      'tw:inline-block tw:flex-shrink-0',
      sizeClass, // Add size classes if provided
      colorClass,
      className
    );

    // Only set width/height attributes if sizeClass is not provided
    const sizeProps = sizeClass
      ? {} // Let Tailwind classes handle sizing
      : { width: size, height: size }; // Use numeric values

    return (
      <IconComponent
        ref={ref as any}
        {...sizeProps}
        className={baseClasses}
        strokeWidth={strokeWidth}
        {...props}
      />
    );
  }
);

Icon.displayName = 'Icon';

export { Icon };
